import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_ARTICLES = [
  { slug: "positioned-incorrectly", title: "Why Most People Are Positioned Incorrectly in the Job Market", subtitle: "The gap between your capabilities and how you present them.", date: "February 2025", readTime: "6 min read", image: "https://images.pexels.com/photos/5811486/pexels-photo-5811486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "clarity-before-action", title: "Clarity Before Action: The Missing Step in Career Growth", subtitle: "Why doing more without direction amplifies confusion.", date: "January 2025", readTime: "5 min read", image: "https://images.unsplash.com/photo-1625645262499-c2a1e2eb09a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { slug: "not-stuck-unclear", title: "You Are Not Stuck — You Are Unclear", subtitle: "The distinction that changes everything about how you move forward.", date: "December 2024", readTime: "4 min read", image: "https://images.unsplash.com/photo-1622579522112-3b69c47d6427?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

export default function InsightsPage() {
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);

  useEffect(() => {
    axios.get(`${API}/articles`).then(res => { if (res.data.length > 0) setArticles(res.data); }).catch(() => {});
  }, []);

  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <section className="bg-navy py-24 px-6 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4">
          Insights
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="font-lora text-4xl lg:text-5xl text-ivory font-normal">
          Perspectives on Clarity &amp; Career
        </motion.h1>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }}>
          <div className="w-12 h-px bg-gold mx-auto mt-6"></div>
          <p className="text-ivory/60 font-serif text-base mt-4 max-w-xl mx-auto leading-relaxed">
            Reflections on career navigation, professional positioning, and the psychology of meaningful work.
          </p>
        </motion.div>
      </section>

      {/* Articles */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-3 gap-10">
          {articles.map((article, i) => (
            <motion.article
              key={article.slug}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}
              data-testid={`article-card-${article.slug}`}
            >
              <Link to={`/insights/${article.slug}`} className="group block">
                <div className="overflow-hidden mb-5">
                  <img
                    src={article.imageUrl || article.image}
                    alt={article.title}
                    className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <p className="text-navy/40 text-xs tracking-widest uppercase mb-2">{article.date} · {article.readTime}</p>
                <h2 className="font-lora text-navy text-xl font-normal leading-snug group-hover:text-navy/70 transition-colors mb-2">
                  {article.title}
                </h2>
                {article.subtitle && (
                  <p className="text-navy/50 font-serif text-sm leading-relaxed">{article.subtitle}</p>
                )}
                <p className="mt-4 text-navy/50 text-xs tracking-widest uppercase hover:text-gold transition-colors">
                  Read Article &rarr;
                </p>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Subscription CTA */}
      <section className="py-16 px-6 bg-white border-t border-soft-gray text-center">
        <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-3">Begin Your Journey</p>
        <h2 className="font-lora text-2xl text-navy font-normal mb-2">Ready to Move Forward?</h2>
        <p className="text-navy/50 font-serif text-sm mb-6">Insights are a starting point. Your advisory session is where clarity begins.</p>
        <Link to="/booking" className="inline-flex items-center gap-2 bg-navy text-ivory px-8 py-3 text-sm tracking-widest uppercase hover:bg-navy/90 transition-all">
          Book a Session
        </Link>
      </section>
    </div>
  );
}
